<template>
    <div class="admin-import-questions-dashboard animation-fadeIn">
        <!-- Page Header -->
        <div class="page-header">
            <div class="header-info">
                <h2 class="page-title">智能试题批量导入</h2>
                <p class="page-subtitle">上传 Word 文档进行智能识别，AI 自动拆解题干、选项、答案与知识点</p>
            </div>
            <div class="header-actions">
                <el-button @click="$router.back()">
                    <el-icon>
                        <ArrowLeft />
                    </el-icon> 返回列表
                </el-button>
            </div>
        </div>

        <div class="import-layout">
            <!-- Top Controls Card -->
            <el-card shadow="never" class="modern-card controls-card">
                <template #header>
                    <div class="card-header">
                        <span class="card-title"><el-icon>
                                <MagicStick />
                            </el-icon> 解析配置与上传</span>
                    </div>
                </template>

                <el-form :model="importForm" label-position="top" class="modern-form compact-form">
                    <el-row :gutter="20">
                        <el-col :lg="5" :md="12">
                            <el-form-item label="Word 文档 (.docx)" required>
                                <div class="upload-wrapper-inline">
                                    <input type="file" ref="fileInput" accept=".docx" @change="handleFileChange"
                                        class="hidden-input" id="import-file" />
                                    <label for="import-file" class="custom-upload-label">
                                        <el-icon>
                                            <Document />
                                        </el-icon>
                                        <span>{{ importForm.file ? importForm.file.name : '选择文档...' }}</span>
                                    </label>
                                </div>
                            </el-form-item>
                        </el-col>
                        <el-col :lg="4" :md="12">
                            <el-form-item label="答题卡模式">
                                <el-radio-group v-model="importForm.answerFormat" class="modern-radio-group">
                                    <el-radio-button label="end">卷末</el-radio-button>
                                    <el-radio-button label="inline">随题</el-radio-button>
                                </el-radio-group>
                            </el-form-item>
                        </el-col>
                        <el-col :lg="3" :md="8">
                            <el-form-item label="默认年级">
                                <el-select v-model="importForm.grade" style="width: 100%">
                                    <el-option label="高一" :value="10" />
                                    <el-option label="高二" :value="11" />
                                    <el-option label="高三" :value="12" />
                                </el-select>
                            </el-form-item>
                        </el-col>
                        <el-col :lg="3" :md="8">
                            <el-form-item label="默认难度">
                                <el-select v-model="importForm.difficult" style="width: 100%">
                                    <el-option label="容易" :value="1" />
                                    <el-option label="适中" :value="3" />
                                    <el-option label="困难" :value="5" />
                                </el-select>
                            </el-form-item>
                        </el-col>
                        <el-col :span="8">
                            <el-form-item label="默认试卷类型/场景">
                                <el-select v-model="importForm.category" style="width: 100%">
                                    <el-option label="同步卷" value="同步卷" />
                                    <el-option label="月考卷" value="月考卷" />
                                    <el-option label="期中卷" value="期中卷" />
                                    <el-option label="期末卷" value="期末卷" />
                                    <el-option label="模拟卷" value="模拟卷" />
                                    <el-option label="真题卷" value="真题卷" />
                                </el-select>
                            </el-form-item>
                        </el-col>
                        <el-col :span="8">
                            <el-form-item label="默认学期">
                                <el-select v-model="importForm.semester" style="width: 100%">
                                    <el-option label="上学期" value="上学期" />
                                    <el-option label="下学期" value="下学期" />
                                </el-select>
                            </el-form-item>
                        </el-col>
                        <el-col :lg="5" :md="8">
                            <el-form-item label="来源/背景">
                                <el-input v-model="importForm.source" placeholder="如：2024期末" />
                            </el-form-item>
                        </el-col>
                        <el-col :lg="4" :md="8">
                            <el-form-item label="默认地区">
                                <el-cascader v-model="importForm.area" :options="pcTextArr" placeholder="选择地区"
                                    style="width: 100%" clearable />
                            </el-form-item>
                        </el-col>
                    </el-row>

                    <el-row :gutter="20">
                        <el-col :span="12">
                            <el-form-item label="默认教材版本">
                                <el-select v-model="importForm.textbookVersionId" style="width: 100%"
                                    @change="handleVersionChange" placeholder="选择版本">
                                    <el-option v-for="v in textbookVersions" :key="v.id" :label="v.name"
                                        :value="v.id" />
                                </el-select>
                            </el-form-item>
                        </el-col>
                        <el-col :span="12">
                            <el-form-item label="默认教材">
                                <el-select v-model="importForm.textbookId" style="width: 100%" placeholder="选择教材">
                                    <el-option v-for="t in filteredTextbooks" :key="t.id" :label="t.name"
                                        :value="t.id" />
                                </el-select>
                            </el-form-item>
                        </el-col>
                    </el-row>
                    <div class="form-actions-row">
                        <div class="ai-toggle-row">
                            <el-switch v-model="enableAI" active-text="开启 AI 知识点分析" inactive-text="关闭"
                                active-color="#409eff" />
                            <span class="ai-hint" v-if="enableAI">⚠️ 开启后解析较慢（DeepSeek 逐题分析）</span>
                            <span class="ai-hint safe" v-else">✅ 关闭 AI，解析快速不超时</span>
                        </div>
                        <el-button type="primary" size="large" @click="startParsing" :loading="parsing"
                            class="parse-btn">
                            <el-icon v-if="!parsing">
                                <Position />
                            </el-icon>
                            {{ parsing ? 'AI 正在深度分析中...' : '提交解析' }}
                        </el-button>
                    </div>
                </el-form>
            </el-card>

            <!-- Results Section -->
            <div class="results-section" style="margin-top: 24px">
                <el-card shadow="never" class="modern-card results-card">
                    <template #header>
                        <div class="card-header">
                            <div class="header-left">
                                <span class="card-title"><el-icon>
                                        <View />
                                    </el-icon> 解析结果预审核</span>
                                <el-tag v-if="questions.length" type="primary" round style="margin-left: 12px">找到 {{
                                    questions.length }} 题</el-tag>
                            </div>
                            <el-button v-if="questions.length" type="success" @click="saveAll" :loading="saving">
                                <el-icon>
                                    <CircleCheck />
                                </el-icon> 确认全部并入库
                            </el-button>
                        </div>
                    </template>

                    <el-empty v-if="questions.length === 0" description="暂无数据，请在上方上传并解析文档" />

                    <div v-else class="question-list-scroll">
                        <div v-for="(q, index) in questions" :key="index" class="import-question-item">
                            <div class="iq-header">
                                <div class="iq-idx">题目 #{{ index + 1 }}</div>
                                <div class="iq-meta-controls">
                                    <el-select v-model="q.type" size="small" style="width: 100px">
                                        <el-option label="单选题" :value="1" />
                                        <el-option label="填空题" :value="2" />
                                        <el-option label="解答题" :value="3" />
                                        <el-option label="多选题" :value="6" />
                                    </el-select>
                                    <el-select v-model="q.difficult" size="small" style="width: 80px">
                                        <el-option label="容易" :value="1" />
                                        <el-option label="适中" :value="3" />
                                        <el-option label="困难" :value="5" />
                                    </el-select>
                                    <el-select v-model="q.semester" size="small" style="width: 90px">
                                        <el-option label="上学期" value="上学期" />
                                        <el-option label="下学期" value="下学期" />
                                    </el-select>
                                    <el-select v-model="q.category" size="small" style="width: 100px" placeholder="场景">
                                        <el-option label="同步卷" value="同步卷" />
                                        <el-option label="月考卷" value="月考卷" />
                                        <el-option label="期中卷" value="期中卷" />
                                        <el-option label="期末卷" value="期末卷" />
                                        <el-option label="模拟卷" value="模拟卷" />
                                        <el-option label="真题卷" value="真题卷" />
                                    </el-select>
                                    <el-cascader v-model="q.area" :options="pcTextArr" placeholder="地区" size="small"
                                        style="width: 140px;" clearable />
                                    <el-input v-model="q.source" size="small" placeholder="来源" style="width: 140px" />
                                    <el-select v-model="q.textbookVersionId" size="small" style="width: 120px"
                                        @change="() => q.textbookId = null" placeholder="教材版本">
                                        <el-option v-for="v in textbookVersions" :key="v.id" :label="v.name"
                                            :value="v.id" />
                                    </el-select>
                                    <el-select v-model="q.textbookId" size="small" style="width: 120px"
                                        placeholder="教材">
                                        <el-option
                                            v-for="t in (q.textbookVersionId ? textbooks.filter(t => t.textbookVersionId === q.textbookVersionId) : textbooks)"
                                            :key="t.id" :label="t.name" :value="t.id" />
                                    </el-select>
                                    <el-button type="danger" link @click="removeQuestion(index)"><el-icon>
                                            <Delete />
                                        </el-icon></el-button>
                                    <el-button type="primary" link size="small" @click="analyzeKP(index)"
                                        :loading="q.analyzingKP"
                                        title="使用 DeepSeek AI 分析本题知识点">
                                        <el-icon v-if="!q.analyzingKP"><Connection /></el-icon>
                                        {{ q.analyzingKP ? '分析中...' : 'AI 知识点' }}
                                    </el-button>
                                </div>
                            </div>

                            <div class="iq-body">
                                <el-row :gutter="20">
                                    <el-col :span="24">
                                        <div class="field-block">
                                            <div class="f-label">题干内容</div>
                                            <MdEditor v-model="q.content" preview-theme="cyanosis"
                                                style="height: 240px;" />
                                        </div>
                                        <div class="field-block" v-if="q.options && q.options.length">
                                            <div class="f-label">选项列表</div>
                                            <div class="opt-edit-list">
                                                <div v-for="(opt, i) in q.options" :key="i" class="opt-edit-item">
                                                    <span class="opt-sym">{{ opt.Label }}</span>
                                                    <el-input v-model="opt.Content" size="small" />
                                                </div>
                                            </div>
                                        </div>
                                    </el-col>
                                    <el-col :span="24">
                                        <div class="field-block">
                                            <div class="f-label">答案 & 详解</div>
                                            <MdEditor v-model="q.answer" preview-theme="cyanosis" style="height: 150px;"
                                                placeholder="答案" />
                                            <MdEditor v-model="q.analysis" preview-theme="cyanosis"
                                                style="height: 150px; margin-top: 12px" placeholder="分析（试题解析第一层）" />
                                            <MdEditor v-model="q.explanation" preview-theme="cyanosis"
                                                style="height: 180px; margin-top: 12px" placeholder="详细解析（详解步骤）" />
                                            <MdEditor v-model="q.keyPoints" preview-theme="cyanosis"
                                                style="height: 150px; margin-top: 12px" placeholder="点睛（方法总结）" />
                                        </div>
                                        <div class="field-block">
                                            <div class="f-label">AI 识别知识点</div>
                                            <div class="kp-edit-box">
                                                <el-tag v-for="(kp, kpi) in q.knowledgePoints" :key="kpi" closable
                                                    @close="removeKp(index, kpi)" class="kp-tag">
                                                    {{ kp.name }}
                                                </el-tag>
                                                <el-autocomplete v-model="q.newKpInput"
                                                    :fetch-suggestions="querySearchKp" placeholder="搜索增加知识点..."
                                                    size="small" @select="(item) => handleSelectKp(index, item)"
                                                    style="width: 160px;">
                                                    <template #prefix><el-icon>
                                                            <Search />
                                                        </el-icon></template>
                                                </el-autocomplete>
                                            </div>
                                        </div>
                                    </el-col>
                                </el-row>
                            </div>
                        </div>
                    </div>
                </el-card>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
    ArrowLeft, Document, MagicStick, View, Position,
    CircleCheck, Delete, Search, CollectionTag, Connection
} from '@element-plus/icons-vue'
import api, { getTextbookVersions } from '@/utils/api'
import { pcTextArr } from 'element-china-area-data'
import MdEditor from 'md-editor-v3';
import 'md-editor-v3/lib/style.css';

const router = useRouter()
const parsing = ref(false)
const parsingTaskId = ref(null)
const pollTimer = ref(null)
const saving = ref(false)
const questions = ref([])
const allKnowledgePoints = ref([])
const textbookVersions = ref([])
const textbooks = ref([])
const enableAI = ref(false)

const importForm = reactive({
    file: null,
    answerFormat: 'end',
    year: new Date().getFullYear().toString(),
    grade: 10,
    difficult: 3,
    source: '',
    area: [],
    category: '期中卷',
    semester: '上学期',
    textbookVersionId: null,
    textbookId: null
})

const handleFileChange = (e) => {
    const files = e.target.files
    if (files.length > 0) importForm.file = files[0]
}

const fetchKnowledgePoints = async () => {
    try {
        const res = await api.get('/api/knowledgePoints')
        allKnowledgePoints.value = res.data.knowledgePoints || res.data || []
    } catch (err) {
        console.error('KPs missing', err)
    }
}

const fetchTextbookVersions = async () => {
    try {
        const res = await getTextbookVersions()
        textbookVersions.value = res.data.versions || res.data || []
    } catch (err) {
        console.error('Failed to load textbook versions', err)
    }
}

const fetchTextbooks = async () => {
    try {
        const res = await api.get('/api/textbooks')
        textbooks.value = res.data.textbooks || res.data || []
    } catch (err) {
        console.error('Failed to load textbooks', err)
    }
}

// Filter textbooks based on selected textbook version
const filteredTextbooks = computed(() => {
    if (!importForm.textbookVersionId) return textbooks.value
    return textbooks.value.filter(t => t.textbookVersionId === importForm.textbookVersionId)
})

// Handle textbook version change - reset textbook selection
const handleVersionChange = () => {
    importForm.textbookId = null
}

const querySearchKp = (queryString, cb) => {
    const results = queryString
        ? allKnowledgePoints.value.filter(kp => kp.name.toLowerCase().includes(queryString.toLowerCase()))
        : allKnowledgePoints.value
    cb(results.map(kp => ({ value: kp.name, id: kp.id })))
}

const handleSelectKp = (qIndex, item) => {
    const q = questions.value[qIndex]
    if (!q.knowledgePoints.some(kp => kp.id === item.id)) {
        q.knowledgePoints.push({ id: item.id, name: item.value })
    }
    q.newKpInput = ''
}

const removeKp = (qIndex, kpIndex) => {
    questions.value[qIndex].knowledgePoints.splice(kpIndex, 1)
}

const removeQuestion = (index) => {
    questions.value.splice(index, 1)
}

const startParsing = async () => {
    if (!importForm.file) {
        ElMessage.warning('请先选择 Word 文件')
        return
    }
    parsing.value = true
    const formData = new FormData()
    formData.append('file', importForm.file)
    formData.append('answer_format', importForm.answerFormat)
    formData.append('difficult', importForm.difficult)
    formData.append('grade', importForm.grade)
    formData.append('source', importForm.source)
    formData.append('year', importForm.year)
    formData.append('semester', importForm.semester)
    formData.append('category', importForm.category)
    formData.append('textbookVersionId', importForm.textbookVersionId || '')
    formData.append('textbookId', importForm.textbookId || '')
    formData.append('enable_ai', enableAI.value ? 'true' : 'false')
    if (importForm.area && importForm.area.length > 0) {
        formData.append('area', JSON.stringify(importForm.area))
    }

    try {
        const res = await api.post('/api/questions/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (res.data.success && res.data.taskId) {
            parsingTaskId.value = res.data.taskId
            ElMessage.info(res.data.message || '任务已提交，正在排队解析...')
            startPolling()
        } else {
            ElMessage.error(res.data.message || '系统繁忙，提交失败')
            parsing.value = false
        }
    } catch (error) {
        ElMessage.error('无法连接到解析引擎')
        parsing.value = false
    }
}

const startPolling = () => {
    if (pollTimer.value) clearInterval(pollTimer.value)
    pollTimer.value = setInterval(async () => {
        try {
            const res = await api.get(`/api/async-tasks/${parsingTaskId.value}`)
            const { status, questions: resultQuestions, error } = res.data

            if (status === 'completed') {
                clearInterval(pollTimer.value)
                pollTimer.value = null
                questions.value = resultQuestions.map(q => ({
                    ...q,
                    newKpInput: '',
                    knowledgePoints: q.knowledgePoints || [],
                    analysis: q.analysis || '',
                    keyPoints: q.keyPoints || '',
                    tags: q.tags || [],
                    year: q.year || importForm.year,
                    grade: q.grade || importForm.grade,
                    difficult: q.difficult || importForm.difficult,
                    source: q.source || importForm.source,
                    area: [...(importForm.area || [])],
                    category: q.category || importForm.category,
                    semester: q.semester || importForm.semester,
                    textbookVersionId: q.textbookVersionId || importForm.textbookVersionId,
                    textbookId: q.textbookId || importForm.textbookId,
                    analyzingKP: false
                }))
                ElMessage.success(`解析成功，找到 ${questions.value.length} 道题目`)
                parsing.value = false
                parsingTaskId.value = null
            } else if (status === 'failed') {
                clearInterval(pollTimer.value)
                pollTimer.value = null
                ElMessage.error(error || '解析任务失败')
                parsing.value = false
                parsingTaskId.value = null
            }
            // If status is 'pending' or 'processing', continue polling
        } catch (err) {
            console.error('Polling error:', err)
        }
    }, 2000) // Poll every 2 seconds
}

const saveAll = async () => {
    if (questions.value.length === 0) return
    saving.value = true
    try {
        const questionsToSave = questions.value.map(q => ({
            ...q,
            region: q.area?.[0] || '',
            city: q.area?.[1] || '',
            scene: q.category || '', // map to Scene
            semester: q.semester || '',
            textbookVersionId: q.textbookVersionId || null,
            textbookId: q.textbookId || null,
            knowledgePoints: q.knowledgePoints
        }))
        const res = await api.post('/api/questions/import/confirm', { questions: questionsToSave })
        if (res.data.success) {
            ElMessage.success(`成功导入 ${res.data.count} 道题目`)
            router.push('/admin/questions')
        } else {
            ElMessage.error(res.data.message || '入库失败')
        }
    } catch (error) {
        ElMessage.error('提交请求失败')
    } finally {
        saving.value = false
    }
}

onMounted(() => {
    fetchKnowledgePoints()
    fetchTextbookVersions()
    fetchTextbooks()
})

// Per-question AI knowledge point analysis
const analyzeKP = async (index) => {
    const q = questions.value[index]
    if (!q || !q.content) {
        ElMessage.warning('该题目内容为空')
        return
    }
    q.analyzingKP = true
    try {
        const res = await api.post('/api/questions/analyze-kp', {
            content: q.content
        })
        if (res.data.success && res.data.knowledgePoints && res.data.knowledgePoints.length > 0) {
            // Merge new KPs (avoid duplicates)
            const existingNames = new Set(q.knowledgePoints.map(k => k.name))
            res.data.knowledgePoints.forEach(kp => {
                if (!existingNames.has(kp.name)) {
                    q.knowledgePoints.push(kp)
                    existingNames.add(kp.name)
                }
            })
            ElMessage.success(`AI 识别出 ${res.data.knowledgePoints.length} 个知识点`)
        } else {
            ElMessage.info('AI 未识别到相关知识点')
        }
    } catch (err) {
        console.error(err)
        ElMessage.error('AI 分析失败，请检查 DeepSeek 配置')
    } finally {
        q.analyzingKP = false
    }
}
</script>

<style scoped>
.admin-import-questions-dashboard {
    background-color: transparent;
}

.page-header {
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    font-size: 24px;
    font-weight: 700;
}

.page-subtitle {
    color: #606266;
    font-size: 14px;
    margin-top: 4px;
}

.modern-card {
    border: none;
    border-radius: 12px;
}

.card-title {
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
}

.modern-form :deep(.el-form-item__label) {
    font-weight: 600;
    padding-bottom: 4px;
}

.upload-wrapper-inline {
    width: 100%;
}

.hidden-input {
    display: none;
}

.custom-upload-label {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: #f8fafc;
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    overflow: hidden;
}

.custom-upload-label span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 13px;
    color: #606266;
}

.custom-upload-label:hover {
    border-color: #409eff;
    background: #ecf5ff;
}

.ai-toggle-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    padding: 10px 14px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e4e7ed;
}

.ai-hint {
    font-size: 13px;
    color: #e6a23c;
}

.ai-hint.safe {
    color: #67c23a;
}

.form-actions-row {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
}

.parse-btn {
    width: 200px;
}

.question-list-scroll {
    padding-right: 4px;
}

.import-question-item {
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 12px;
    margin-bottom: 24px;
    overflow: hidden;
}

.iq-header {
    background: #f8fafc;
    padding: 12px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #f0f0f0;
}

.iq-idx {
    font-weight: 800;
    color: #409eff;
}

.iq-meta-controls {
    display: flex;
    gap: 12px;
    align-items: center;
}

.iq-body {
    padding: 20px;
}

.field-block {
    margin-bottom: 20px;
}

.f-label {
    font-size: 12px;
    font-weight: 700;
    color: #909399;
    text-transform: uppercase;
    margin-bottom: 8px;
    letter-spacing: 0.5px;
}

.opt-edit-list {
    background: #f8fafc;
    padding: 12px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.opt-edit-item {
    display: flex;
    align-items: center;
    gap: 10px;
}

.opt-sym {
    font-weight: bold;
    color: #409eff;
    width: 20px;
}

.kp-edit-box {
    background: #f8fafc;
    padding: 12px;
    border-radius: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
}

.kp-tag {
    border-radius: 6px;
}

.animation-fadeIn {
    animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
